/**
 * useArTableAggregations
 *
 * React hook that coordinates server-side aggregation computation.
 * Ensures the aggregation row exists, marks it stale on data changes,
 * and triggers the computeAggregations Cloud Function with debouncing.
 * Results arrive via the existing onSnapshot listener.
 *
 * Does nothing while the table has no data rows: there is nothing to
 * aggregate, so it neither writes the aggregation row nor calls the CF. It
 * re-arms when the first row is added. (This also keeps the layout-editor
 * preview quiet — its tables sit on an empty sentinel path, so they never
 * compute against a form version that does not exist.)
 *
 * The aggregation row has two Firestore writers into the same `rowsState.docs`
 * entry: persistCollection when pagination includes it, and this hook's
 * dedicated onSnapshot which keeps it live when pagination excludes it. That
 * listener has no modifiedAt/write-queue gate, so it must only ever merge into
 * an existing entry, never replace the entry object.
 *
 * Invariant (SYD-2311): the setup signature that dedupes repeated `setup()` calls
 * is effect-local. A ref would survive the effect's own teardown, so the re-run
 * that follows a rows-store rebuild (page or sort change hands the hook a new
 * persist) would match the stale signature, return before subscribing, and leave
 * the table with no trigger and no aggregation-doc listener at all.
 *
 * Second invariant (SYD-2311): the aggregation row is seeded only when the
 * dedicated listener reports the server document missing, never when the local
 * slot is missing. A store rebuild empties `docs`, and seeding on that reads as a
 * recompute of the whole table on every page turn.
 */

import { useEffect } from 'react';
import { Timestamp, doc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { subscribe } from 'valtio';
import { subscribeKey } from 'valtio/utils';
import debounce from 'lodash/debounce';
import { useStoreNonReactive } from '@/store';
import { firestore } from '@/services/startFirestore';
import { AR_TABLE_AGGREGATIONS_ROW_ID, arTableRowsPath } from '#types/System.Artifacts.types';
import type {
  ArColumnDefinition,
  ArColumnField,
  ArRowId,
  ArRowData,
  ArRowDocument,
  ArTableType,
} from '#types/System.Artifacts.types';
import { UIElementType } from '#types/System.types';
import type { CollectionPersist, CollectionState, DocumentState } from '../Store.types';
import { mergeDocumentIntoProxy } from '../core/mergeDocumentIntoProxy';

const DEBOUNCE_MS = 2_000;

function hasAggregationColumns(columns: ArColumnDefinition[]): boolean {
  return columns.some(
    (col) => col.uiElementType === UIElementType.Field && !!(col as ArColumnField).aggregation
  );
}

function useArTableAggregations(parentPath: string, tableId: string): void {
  const { state: schemaStateRaw } = useStoreNonReactive('schema');
  const { state: rowsStateRaw, persist: rowsPersistRaw } = useStoreNonReactive('rows');

  const schemaState = schemaStateRaw as DocumentState<ArTableType>;
  const rowsState = rowsStateRaw as CollectionState<ArRowDocument>;
  const rowsPersist = rowsPersistRaw as CollectionPersist<ArRowDocument>;

  useEffect(() => {
    if (rowsPersist.persistType !== 'collection') return;

    let activeSignature = '';
    let debouncedTrigger: ReturnType<typeof debounce> | null = null;
    let unsubscribeDocs: (() => void) | null = null;
    let unsubscribeAggregationDoc: (() => void) | null = null;
    const rowSubscriptions = new Map<ArRowId, { entry: unknown; unsubscribe: () => void }>();
    let setupVersion = 0;

    const triggerComputation = async (): Promise<void> => {
      try {
        const { computeAggregations } = await import('@/services/arTableFunctions');
        await computeAggregations({ parentPath, tableId });
      } catch (err) {
        console.error('[useArTableAggregations] CF call failed:', err);
      }
    };

    const markStaleAndTrigger = (): void => {
      const aggEntry = rowsState.docs.get(AR_TABLE_AGGREGATIONS_ROW_ID);
      const isAlreadyStale = aggEntry?.data?.upToDate === false;
      if (!isAlreadyStale && aggEntry?.data) {
        aggEntry.data.upToDate = false;

        void rowsPersist.setMerge(
          AR_TABLE_AGGREGATIONS_ROW_ID,
          { upToDate: false },
          { debounce: false }
        );
      }

      debouncedTrigger?.();
    };

    const cleanupAll = (): void => {
      setupVersion++;
      debouncedTrigger?.cancel();
      debouncedTrigger = null;
      unsubscribeDocs?.();
      unsubscribeDocs = null;
      unsubscribeAggregationDoc?.();
      unsubscribeAggregationDoc = null;
      for (const sub of rowSubscriptions.values()) {
        sub.unsubscribe();
      }
      rowSubscriptions.clear();
    };

    const setup = async (): Promise<void> => {
      if (schemaState.loading || rowsState.loading) return;

      const aggregationsEnabled = schemaState.data?.aggregationsEnabled === true;
      const columns = schemaState.data?.columns ?? [];
      const hasAgg = aggregationsEnabled && hasAggregationColumns(columns);
      const aggRowExists = rowsState.docs.has(AR_TABLE_AGGREGATIONS_ROW_ID);
      const hasRows = Array.from(rowsState.docs.keys()).some(
        (rowId) => rowId !== AR_TABLE_AGGREGATIONS_ROW_ID
      );
      const signature = JSON.stringify({ parentPath, tableId, aggregationsEnabled, hasAgg, hasRows });

      if (signature === activeSignature) return;
      const isReconfiguration = activeSignature !== '';
      activeSignature = signature;

      cleanupAll();

      if (!hasAgg) return;
      if (!hasRows && !aggRowExists) return;

      const currentVersion = setupVersion;

      /**
       * Settles with the document's server-side existence on the listener's first
       * delivery. `docs.has(...)` cannot answer that question: a store rebuild
       * empties `docs`, so a dropped slot is indistinguishable from a document
       * that was never written, and seeding on the former rewrites the row and
       * recomputes the whole table on every page or sort change (SYD-2311).
       */
      let settleFirstSnapshot: ((exists: boolean) => void) | null = null;
      const serverDocExists = new Promise<boolean>((resolve) => {
        settleFirstSnapshot = resolve;
      });

      unsubscribeAggregationDoc = onSnapshot(
        doc(firestore, `${arTableRowsPath(parentPath, tableId)}/${AR_TABLE_AGGREGATIONS_ROW_ID}`),
        (snapshot) => {
          settleFirstSnapshot?.(snapshot.exists());
          settleFirstSnapshot = null;
          if (!snapshot.exists()) return;
          const incoming = snapshot.data() as ArRowData;
          const existing = rowsState.docs.get(AR_TABLE_AGGREGATIONS_ROW_ID);
          if (existing) {
            mergeDocumentIntoProxy(
              existing.data,
              incoming,
              AR_TABLE_AGGREGATIONS_ROW_ID
            );
          } else {
            rowsState.docs.set(AR_TABLE_AGGREGATIONS_ROW_ID, {
              data: { ...incoming, docId: AR_TABLE_AGGREGATIONS_ROW_ID },
            });
          }
        },
        (err) => {
          console.error('[useArTableAggregations] aggregation listener failed:', err);
          settleFirstSnapshot?.(true);
          settleFirstSnapshot = null;
        }
      );

      debouncedTrigger = debounce(triggerComputation, DEBOUNCE_MS);

      const syncRowSubscriptions = (): boolean => {
        const wanted = new Set<ArRowId>(
          Array.from(rowsState.docs.keys()).filter((rowId) => rowId !== AR_TABLE_AGGREGATIONS_ROW_ID) as ArRowId[]
        );
        let didChange = false;

        for (const [rowId, sub] of rowSubscriptions) {
          if (wanted.has(rowId)) continue;
          sub.unsubscribe();
          rowSubscriptions.delete(rowId);
          didChange = true;
        }

        for (const rowId of wanted) {
          const rowEntry = rowsState.docs.get(rowId);
          const existing = rowSubscriptions.get(rowId);

          if (!rowEntry) {
            if (!existing) continue;
            existing.unsubscribe();
            rowSubscriptions.delete(rowId);
            didChange = true;
            continue;
          }

          if (existing?.entry === rowEntry) continue;

          existing?.unsubscribe();
          rowSubscriptions.set(rowId, {
            entry: rowEntry,
            unsubscribe: subscribe(rowEntry.data as unknown as object, markStaleAndTrigger),
          });
          didChange = true;
        }

        return didChange;
      };

      syncRowSubscriptions();

      unsubscribeDocs = subscribe(rowsState.docs as unknown as object, () => {
        if (!syncRowSubscriptions()) return;
        markStaleAndTrigger();
      });

      /**
       * Watchers are armed before this await, so a mutation racing the listener's
       * first delivery still marks the row stale and reaches the CF.
       */
      const documentExists = await serverDocExists;
      if (currentVersion !== setupVersion) return;

      if (!documentExists) {
        rowsState.docs.set(AR_TABLE_AGGREGATIONS_ROW_ID, {
          data: {
            docId: AR_TABLE_AGGREGATIONS_ROW_ID,
            cells: {},
            annotation: '__system__',
            upToDate: false,
            createdAt: Timestamp.now(),
            modifiedAt: Timestamp.now(),
          },
        });

        try {
          await rowsPersist.setMerge(
            AR_TABLE_AGGREGATIONS_ROW_ID,
            {
              cells: {},
              annotation: '__system__',
              upToDate: false,
              createdAt: serverTimestamp(),
              modifiedAt: serverTimestamp(),
            } as Partial<ArRowDocument>,
            { debounce: false }
          );
        } catch (err) {
          console.error('[useArTableAggregations] setMerge failed:', err);
          return;
        }

        if (currentVersion !== setupVersion) return;

        void triggerComputation();
        return;
      }

      if (rowsState.docs.get(AR_TABLE_AGGREGATIONS_ROW_ID)?.data?.upToDate === false) {
        void triggerComputation();
      } else if (isReconfiguration) {
        markStaleAndTrigger();
      }
    };

    void setup();

    const unsubscribeSchema = subscribe(schemaState as unknown as object, () => void setup());
    const unsubscribeRowsLoading = subscribeKey(rowsState as unknown as object, 'loading' as never, () => void setup());
    const unsubscribeRowsPresence = subscribe(rowsState.docs as unknown as object, () => void setup());

    return () => {
      unsubscribeSchema();
      unsubscribeRowsLoading();
      unsubscribeRowsPresence();
      cleanupAll();
    };
  }, [parentPath, tableId, rowsPersist, rowsState, schemaState]);
}

export { useArTableAggregations };
